import { useState } from 'react';
import { Box, Stack } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';

import CompaniesTab from './companies/CompaniesTab';
import UnprocessedValuationUpdatesTab from 'src/secret-menu/bulk-valuations/UnprocessedValuationUpdatesTab';
import { Navigate, useParams } from 'react-router';
import TemplatesListPage from 'src/company/templates/components/TemplatesListPage';
import DeprecatedTemplateTab from 'src/secret-menu/templates/DeprecatedTemplateTab';
import FeatureFlagsTab from 'src/secret-menu/feature-flags/FeatureFlagsTab';
import ModelArenaTab from 'src/secret-menu/model-arena/ModelArenaTab';
import FeatureFlagsPage from 'src/secret-menu/feature-flags/FeatureFlagsPage';
import FlagMatrixPage from 'src/secret-menu/feature-flags/FlagMatrixPage';
import CompanyUsersPage from 'src/secret-menu/company-users/CompanyUsersPage';
import EditorPlaygroundTab from './editor-playground/EditorPlaygroundTab';
import CompanyClientsPage from 'src/secret-menu/company-clients/CompanyClientsPage';
import FilesTab from 'src/secret-menu/files/FilesTab';
import GeneratedDocumentsTab from 'src/secret-menu/generated-documents/GeneratedDocumentsTab';
import BackgroundProcessesTab from 'src/secret-menu/background-processes/BackgroundProcessesTab';
import TenantSettingsTab from 'src/secret-menu/tenant-settings/TenantSettingsTab';
import SharepointTab from 'src/secret-menu/sharepoint/SharepointTab';
import InfoTab from 'src/secret-menu/info/InfoTab';
import SandboxTab from 'src/secret-menu/sandbox/SandboxTab';
import TopicTreeTab from 'src/secret-menu/topic-tree/TopicTreeTab';
import LlmConfigTab from './llm-config/LlmConfigTab';
import LlmDispatcherTab from './llm-dispatcher/LlmDispatcherTab';
import CustomSkillsTab from './custom-skills/CustomSkillsTab';
import ProductKnowledgeTab from './product-knowledge/ProductKnowledgeTab';
import ExportAssetsTab from 'src/secret-menu/export-assets/ExportAssetsTab';
import AutoBrandTab from 'src/secret-menu/auto-brand/AutoBrandTab';
import BotMigrationTab from 'src/secret-menu/bot-migration/BotMigrationTab';
import ReprocessWrangledTab from 'src/secret-menu/reprocess-wrangled/ReprocessWrangledTab';
import CalendarHealthTab from 'src/secret-menu/calendar-health/CalendarHealthTab';
import HealthCheckTab from 'src/secret-menu/health-check/HealthCheckTab';
import ConversationDebuggerTab from 'src/secret-menu/conversation-debugger/ConversationDebuggerTab';
import TranscriptTesterTab from 'src/secret-menu/transcript-tester/TranscriptTesterTab';
import OpenBankingTab from 'src/secret-menu/open-banking/OpenBankingTab';
import DeriveFrnTestTab from 'src/secret-menu/derive-frn-test/DeriveFrnTestTab';
import AutoBrandFromUrlTab from 'src/secret-menu/auto-brand-from-url/AutoBrandFromUrlTab';
import JoinRequestsTab from 'src/secret-menu/join-requests/JoinRequestsTab';
import JoinRequestDrawer from 'src/secret-menu/join-requests/JoinRequestDrawer';
import TenantApiKeysTab from 'src/secret-menu/tenant-api-keys/TenantApiKeysTab';
import MicrosoftEmailTab from 'src/secret-menu/microsoft-email/MicrosoftEmailTab';
import BillingTab from 'src/secret-menu/billing/BillingTab';
import SecretMenuCommandPalette from 'src/secret-menu/navigation/SecretMenuCommandPalette';
import SecretMenuIndex from 'src/secret-menu/navigation/SecretMenuIndex';
import SecretMenuToolHeader from 'src/secret-menu/navigation/SecretMenuToolHeader';
import { SecretMenuTab, SecretMenuTabs } from 'src/secret-menu/navigation/secretMenuCatalogue';
import { useSecretMenuTools } from 'src/secret-menu/navigation/useSecretMenuTools';

export default function SecretMenuPage() {
  const { tab } = useParams<{ tab: string }>();
  const { tools, findAvailableTool } = useSecretMenuTools();
  const [selectedJoinRequestId, setSelectedJoinRequestId] = useState<string | null>(null);
  const [paletteOpened, { open: openPalette, close: closePalette }] = useDisclosure(false);

  useHotkeys([['mod+K', openPalette]], []);

  const palette = (
    <SecretMenuCommandPalette tools={tools} opened={paletteOpened} onClose={closePalette} />
  );

  if (!tab) {
    return (
      <>
        <SecretMenuIndex tools={tools} />
        {palette}
      </>
    );
  }

  const tool = findAvailableTool(tab);

  if (!tool) {
    return <Navigate to="/secret-menu" replace />;
  }

  const renderContent = (slug: SecretMenuTab) => {
    switch (slug) {
      case SecretMenuTabs.companies:
        return <CompaniesTab />;
      case SecretMenuTabs.companyUsers:
        return <CompanyUsersPage />;
      case SecretMenuTabs.companyClients:
        return <CompanyClientsPage />;
      case SecretMenuTabs.templates:
        return <TemplatesListPage />;
      case SecretMenuTabs.oldTemplates:
        return <DeprecatedTemplateTab />;
      case SecretMenuTabs.bulkValuations:
        return <UnprocessedValuationUpdatesTab />;
      case SecretMenuTabs.files:
        return <FilesTab />;
      case SecretMenuTabs.generatedDocuments:
        return <GeneratedDocumentsTab />;
      case SecretMenuTabs.backgroundProcesses:
        return <BackgroundProcessesTab />;
      case SecretMenuTabs.featureFlags:
        return <FeatureFlagsTab />;
      case SecretMenuTabs.ldFlagStatuses:
        return <FeatureFlagsPage />;
      case SecretMenuTabs.flagMatrix:
        return <FlagMatrixPage />;
      case SecretMenuTabs.editorPlayground:
        return <EditorPlaygroundTab />;
      case SecretMenuTabs.tenantSettings:
        return <TenantSettingsTab />;
      case SecretMenuTabs.sharepoint:
        return <SharepointTab />;
      case SecretMenuTabs.info:
        return <InfoTab />;
      case SecretMenuTabs.calendarHealth:
        return <CalendarHealthTab />;
      case SecretMenuTabs.healthCheck:
        return <HealthCheckTab />;
      case SecretMenuTabs.sandbox:
        return <SandboxTab />;
      case SecretMenuTabs.topicTree:
        return <TopicTreeTab />;
      case SecretMenuTabs.llmConfig:
        return <LlmConfigTab />;
      case SecretMenuTabs.llmDispatcher:
        return <LlmDispatcherTab />;
      case SecretMenuTabs.modelArena:
        return <ModelArenaTab />;
      case SecretMenuTabs.exportAssets:
        return <ExportAssetsTab />;
      case SecretMenuTabs.autoBrand:
        return <AutoBrandTab />;
      case SecretMenuTabs.productKnowledge:
        return <ProductKnowledgeTab />;
      case SecretMenuTabs.customSkills:
        return <CustomSkillsTab />;
      case SecretMenuTabs.botMigration:
        return <BotMigrationTab />;
      case SecretMenuTabs.reprocessWrangled:
        return <ReprocessWrangledTab />;
      case SecretMenuTabs.conversationDebugger:
        return <ConversationDebuggerTab />;
      case SecretMenuTabs.transcriptTester:
        return <TranscriptTesterTab />;
      case SecretMenuTabs.openBanking:
        return <OpenBankingTab />;
      case SecretMenuTabs.deriveFrnTest:
        return <DeriveFrnTestTab />;
      case SecretMenuTabs.autoBrandFromUrl:
        return <AutoBrandFromUrlTab />;
      case SecretMenuTabs.joinRequests:
        return (
          <JoinRequestsTab
            onSelectRequest={setSelectedJoinRequestId}
            onRejectRequest={setSelectedJoinRequestId}
          />
        );
      case SecretMenuTabs.tenantApiKeys:
        return <TenantApiKeysTab />;
      case SecretMenuTabs.microsoftEmail:
        return <MicrosoftEmailTab />;
      case SecretMenuTabs.billing:
        return <BillingTab />;
    }
  };

  return (
    <>
      <Stack gap="xs">
        <SecretMenuToolHeader tool={tool} onSearch={openPalette} />
        <Box>{renderContent(tool.slug)}</Box>
      </Stack>
      <JoinRequestDrawer
        requestId={selectedJoinRequestId}
        onClose={() => setSelectedJoinRequestId(null)}
      />
      {palette}
    </>
  );
}
